import { Container, Text } from "@react-three/uikit";
import { useEffect, useState } from "react";

export interface VolumeOverlayProps {
  volume: number; // 0-40
  visible: boolean;
  onHide?: () => void;
  autoHideDelay?: number; // ms before auto-hiding (default: 2000)
}

const MAX_VOLUME = 40;
const BAR_COUNT = 40;

/**
 * Retro CRT-style volume overlay
 * Simple bar display at bottom of screen
 */
export function VolumeOverlay({
  volume,
  visible,
  onHide,
  autoHideDelay = 2000,
}: VolumeOverlayProps) {
  const [opacity, setOpacity] = useState(0);

  // Fade in/out animation
  useEffect(() => {
    if (visible) {
      setOpacity(1);
    } else {
      setOpacity(0);
    }
  }, [visible]);

  // Auto-hide after delay
  useEffect(() => {
    if (visible && onHide) {
      const timer = setTimeout(() => {
        onHide();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }
  }, [visible, volume, onHide, autoHideDelay]);

  if (!visible && opacity === 0) {
    return null;
  }

  const clampedVolume = Math.max(0, Math.min(MAX_VOLUME, Math.round(volume)));
  // Map volume (0-40) to bars (0-20)
  const activeBars = Math.round((clampedVolume / MAX_VOLUME) * BAR_COUNT);

  return (
    <Container
      positionType="absolute"
      positionBottom={100}
      positionLeft={60}
      positionRight={60}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      gap={12}
      opacity={opacity}
    >
      {/* Volume number */}
      <Text
        fontSize={48}
        fontWeight="bold"
        color="#00E500"
        fontFamily="monospace"
      >
        {clampedVolume.toString().padStart(2, "0")}
      </Text>

      {/* Volume bars - heights increase left to right */}
      <Container flexDirection="row" gap={8} alignItems="center">
        {Array.from({ length: BAR_COUNT }).map((_, i) => {
          const isActive = i < activeBars;
          // Bar height increases from left to right
          const minHeight = 18;
          const maxHeight = 52;
          const barHeight = isActive ? maxHeight : minHeight;

          return (
            <Container
              key={i}
              width={16}
              height={barHeight}
              marginLeft={i === 0 ? 0 : 2}
              marginRight={i === BAR_COUNT - 1 ? 0 : 2}
              backgroundColor={isActive ? "#00E500" : "#00E500"} // "#003300"}
            />
          );
        })}
      </Container>
    </Container>
  );
}
